<template>
  <Card>
    <Toolbar class="sub-header ag-sub-header">
      <div class="mr-4 subheading">
        {{ $t(graphName) }}
      </div>
      <v-spacer></v-spacer>
      <div class="body-1">
        {{ showDate }}
      </div>
    </Toolbar>
    <v-container>
      <v-layout row wrap align-content-space-between>

        <v-flex xs6 lg2>
          <Select
            :label="$t('Select Graph')"
            :items="graphCategoryList"
            :selection="graphCategory"
            :itemText="'text'"
            :itemValue="'value'"
            @change="graphChangeHandler"
          />
        </v-flex>
      </v-layout>
      <div class="graph">
        <GlobalGraph
          class="graph"
          v-if="series.length"
          :series="series"
          :xAxisData="xAxis"
          :yAxis="yAxis"
          :theme="theme"
        />
      </div>
    </v-container>
  </Card>
</template>

<script>
import PcsServices from "@/services/Pcs.js";
import { mapGetters } from "vuex";
import { mixin, mapGettersMixin } from "@/util/mixin/index";
import GlobalGraph from "@/components/common/GlobalGraph.vue";
const Toolbar = () => import("@/components/common/Toolbar");
const moment = require("moment");
const momentTz = require("moment-timezone");

export default {
  components: {
    GlobalGraph,
    Toolbar,
  },
  props: ["fullDate", "selectedPcsId"],
  mixins: [mixin, mapGettersMixin],
  data() {
    return {
      graphCategory: "actv_pwr_totl",
      series: [],
      xAxis: [],
      yAxis: [],
      mfmFeMap: {
        actv_pwr_totl: { name: "Active Power Total", unit: "kW"},
        reactv_pwr_totl: { name: "Reactive Power Total", unit: "kVAR"},
        aprt_pwr_totl: { name: "Apparent Power Total", unit: "kVA"},
      },
      mqttCallback: null,
    };
  },
  computed: {
    ...mapGetters([
      "getSelectedPlantId",
      "getGeneralState",
      "getActivePlantInfo",
    ]),
    activeFilter() {
      return this.getGeneralState("activeFilterOption");
    },
    showDate() {
      if (this.fullDate && this.fullDate[0] && this.fullDate[1]) {
        if (this.fullDate[0] === this.fullDate[1]) {
          return `${moment(this.fullDate[0]).format('YYYY/M/D')}`
        }
        return `${moment(this.fullDate[0]).format('YYYY/M/D')} - ${moment(this.fullDate[1]).format('YYYY/M/D')}`
      }
      return ""
    },
    graphName() {
      return `${this.mfmFeMap[this.graphCategory].name} Graph`;
    },
    graphCategoryList() {
      return [
        {
          text: this.$t("Active Power Total"),
          value: "actv_pwr_totl"
        },
        {
          text: this.$t("Reactive Power Total"),
          value: "reactv_pwr_totl"
        },
        {
          text: this.$t("Apparent Power Total"),
          value: "aprt_pwr_totl"
        },
      ];
    },
  },
  async mounted() {
    this.$emit("loading", true);
    if (this.fullDate && this.fullDate[0]) {
      await this.getGraphData();
    }
    this.setupMqtt();
    this.$emit("loading", false);
  },
  beforeDestroy() {
    this.cleanupMqtt();
  },
  methods: {
    async graphChangeHandler(graph) {
      this.graphCategory = graph;
      this.getGraphData();
    },
    async getGraphData() {
      if (!this.fullDate || !this.fullDate[0]) return;
      this.$emit("loading", true);
      try {
        const plantId = this.getSelectedPlantId;
        const response = await PcsServices.fetchGraphData(
          plantId,
          moment(this.fullDate[0]).format("YYYY-MM-DD"),
          this.selectedPcsId,
          this.graphCategory
        );
        const series = [];
        const xAxis = [];
        if (response.data && Array.isArray(response.data.message)) {
          response.data.message
            .sort((a, b) => a.sensor_time - b.sensor_time)
            .forEach((el) => {
              const time = momentTz(el.sensor_time, "X")
                .tz(this.getActivePlantInfo("timezone"))
                .format("HH:mm");
              xAxis.push(time);
              series.push(el[this.graphCategory]);
            });
        }
        this.xAxis = xAxis;
        this.series = [
          {
            name: `${this.mfmFeMap[this.graphCategory].name} (${this.mfmFeMap[this.graphCategory].unit})`,
            type: "line",
            markPoint: {
              data: [
                { type: "max", name: "Max" },
                { type: "min", name: "Min" },
              ],
            },
            markLine: {
              data: [
                { type: "average", name: "Avg" },
              ],
            },
            data: series,
          },
        ];

        this.yAxis = [
          {
            name: `${this.mfmFeMap[this.graphCategory].name} (${this.mfmFeMap[this.graphCategory].unit})`,
            axisLabel: {
              formatter: "{value}",
              rotate: 40,
            },
          },
        ];
      } catch (error) {
        console.error("Error fetching PCS graph data:", error);
      } finally {
        this.$emit("loading", false);
      }
    },
    setupMqtt() {
      const plantId = this.getSelectedPlantId;
      if (!plantId) return;

      const topic = `live/${plantId}/pcs`;
      this.$mqtt.subscribe(topic);

      if (this.mqttCallback) {
        this.$mqtt.removeListener("message", this.mqttCallback);
      }

      this.mqttCallback = (topic, message) => {
        if (topic.endsWith("/pcs")) {
          try {
            const payload = JSON.parse(message.toString());
            const mqttData = payload.deviceList && payload.deviceList[0];
            if (mqttData && !mqttData.exception) {
              const timezone = this.getActivePlantInfo("timezone");
              const currentMoment = momentTz(mqttData.sensor_time, "X").tz(timezone);
              const currentTimeStr = currentMoment.format('HH:mm');
              
              if (this.xAxis.length > 0) {
                let totalPackets = this.xAxis.length - 1;
                const lastTimeStr = this.xAxis[totalPackets];
                let iteratorTime = momentTz(lastTimeStr, 'HH:mm').tz(timezone);
                
                iteratorTime.add(1, 'minutes');

                while (currentMoment.isSameOrAfter(iteratorTime)) {
                   totalPackets += 1;
                   const isTarget = currentMoment.format('HH:mm') === iteratorTime.format('HH:mm');
                   const parameterData = isTarget ? mqttData[this.graphCategory] : null;
                   const timeStr = iteratorTime.format('HH:mm');

                   this.$set(this.series[0].data, totalPackets, parameterData);
                   this.$set(this.xAxis, totalPackets, timeStr);
                   
                   iteratorTime.add(1, 'minutes');
                }
              }
            }
          } catch (e) {
            console.error("Error parsing PCS graph MQTT message:", e);
          }
        }
      };

      this.$mqtt.on("message", this.mqttCallback);
    },
    cleanupMqtt() {
      const plantId = this.getSelectedPlantId;
      if (plantId) {
        this.$mqtt.unsubscribe(`live/${plantId}/pcs`);
      }
      if (this.mqttCallback) {
        this.$mqtt.removeListener("message", this.mqttCallback);
        this.mqttCallback = null;
      }
    }
  },
  watch: {
    getSelectedPlantId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.graphCategory = "actv_pwr_totl";
        this.cleanupMqtt();
        this.getGraphData().then(() => {
          this.setupMqtt();
        });
      }
    },
    fullDate(newVal) {
      if (newVal && newVal[0]) {
        this.getGraphData();
      }
    },
    selectedPcsId() {
      this.getGraphData();
    }
  },
};
</script>

<style scoped>
.graph {
  width: 100%;
  height: 492px;
}

.toggle-position {
  text-align: center;
}
</style>
